/**
 * Phase 8 — Admin Weekly Winners email controls.
 *  - Preview computed top-5 + rendered HTML
 *  - Send a test email to a single address
 *  - Trigger manual send to all opt-in recipients
 *  - View last run stats + last sent date
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail, Send, Eye, RefreshCw, Trophy, AlertTriangle, Calendar, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const AdminWeeklyWinnersPanel = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [showHtml, setShowHtml] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await ax.get('/api/admin/weekly-winners/preview');
      setPreview(r.data);
    } catch (_e) { toast.error('Preview failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doTestSend = async () => {
    if (!testEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    setSending(true);
    try {
      const r = await ax.post('/api/admin/weekly-winners/test-send', { email: testEmail });
      if (r.data.sent > 0) toast.success(`Test email sent to ${testEmail}`);
      else toast.error(`Send failed — check email service configuration`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally { setSending(false); }
  };

  const doSendNow = async () => {
    const n = preview?.recipient_count || 0;
    if (!window.confirm(`Send Weekly Winners email now to ${n} recipient${n === 1 ? '' : 's'}?`)) return;
    setSending(true);
    try {
      const r = await ax.post('/api/admin/weekly-winners/send-now');
      toast.success(`Sent ${r.data.sent}, failed ${r.data.failed}`);
      await load();
    } catch (_e) { toast.error('Manual send failed'); }
    finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="admin-weekly-winners-panel">
        <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading preview…</div>
      </div>
    );
  }
  const top5 = preview?.top5 || [];
  const last = preview?.last_run;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="admin-weekly-winners-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><Mail className="h-5 w-5 text-red-600" /> Weekly Winners email</h2>
          <p className="text-xs text-gray-500 mt-0.5">Monday 08:00 Australia/Melbourne · Public usernames only · Real names never sent.</p>
        </div>
        <button onClick={load}
          className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 font-bold"
          data-testid="ww-reload">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold flex items-center gap-1"><Trophy className="h-3 w-3" /> Top 5 this week</div>
          <ol className="mt-2 space-y-1 text-sm" data-testid="ww-top5">
            {top5.length === 0 && <li className="text-gray-400 italic text-xs">No public XP earned yet this week.</li>}
            {top5.map((r, i) => (
              <li key={r.username} className="flex items-center justify-between">
                <span className="font-bold">{i + 1}. @{r.username}</span>
                <span className="text-amber-800 font-mono text-xs">{r.week_xp} XP</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="rounded-xl bg-sky-50 border border-sky-100 p-3">
          <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold">Recipients</div>
          <div className="text-3xl font-bold mt-2" data-testid="ww-recipient-count">{preview?.recipient_count ?? 0}</div>
          <div className="text-[11px] text-gray-500">Students with a public handle + not opted out.</div>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold flex items-center gap-1"><Calendar className="h-3 w-3" /> Last run</div>
          {last ? (
            <>
              <div className="text-sm font-bold mt-1" data-testid="ww-last-run">{new Date(last.last_run_at).toLocaleString()}</div>
              <div className="text-[11px] text-gray-600">Sent {last.last_sent} · Failed {last.last_failed}{last.last_test ? ' · test' : ''}</div>
            </>
          ) : (
            <div className="text-xs text-gray-400 italic mt-1">Never run yet</div>
          )}
        </div>
      </div>

      {/* Test send + manual trigger */}
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <div className="rounded-xl border border-gray-200 p-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Send test email</div>
          <div className="flex items-center gap-2 mt-2">
            <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 text-xs px-3 py-2 border rounded-full focus:outline-none focus:border-black"
              data-testid="ww-test-email-input" />
            <button onClick={doTestSend} disabled={sending}
              className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-2 rounded-full bg-black text-[#D3D86F] hover:bg-gray-900 disabled:opacity-40"
              data-testid="ww-test-send-btn">
              <Send className="h-3 w-3" /> Send test
            </button>
          </div>
        </div>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Manual trigger</div>
          <p className="text-[11px] text-gray-600 mt-1">Sends the email now to all opt-in recipients. The scheduled cron still runs on Mondays.</p>
          <button onClick={doSendNow} disabled={sending || (preview?.recipient_count || 0) === 0}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold px-3 py-2 rounded-full bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-40"
            data-testid="ww-send-now-btn">
            <Send className="h-3 w-3" /> Send now to {preview?.recipient_count ?? 0}
          </button>
        </div>
      </div>

      {/* Preview HTML toggle */}
      <div className="rounded-xl border border-gray-200 p-3">
        <button onClick={() => setShowHtml(v => !v)}
          className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-700"
          data-testid="ww-toggle-html">
          <Eye className="h-3 w-3" /> {showHtml ? 'Hide' : 'Show'} email preview
        </button>
        {showHtml && preview?.html_preview && (
          <div className="mt-3 border rounded-xl overflow-hidden bg-white" data-testid="ww-html-preview">
            <iframe title="Weekly Winners preview" srcDoc={preview.html_preview}
              className="w-full" style={{ height: 720, border: 0 }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWeeklyWinnersPanel;
